import {useMemo, useRef, useState, type DragEvent as ReactDragEvent, type DragEventHandler} from 'react'

export type DropTargetProps = {
  onDragEnter: DragEventHandler
  onDragOver: DragEventHandler
  onDragLeave: DragEventHandler
  onDrop: DragEventHandler
}

export type UseFileDropTargetReturn = {
  /** Spread onto the drop target element for inline drag-and-drop support. */
  dropTargetProps: DropTargetProps
  /** True when a file is being dragged over the drop target. Use for visual feedback. */
  isDraggedOver: boolean
}

/**
 * Provides drag-and-drop handlers for a file drop target, forwarding dropped files
 * to `onFilesDropped`.
 *
 * Uses an enter/leave counter because `dragleave` also fires when the cursor moves
 * onto a child element, which would otherwise flicker `isDraggedOver` off.
 */
export function useFileDropTarget(onFilesDropped: (files: FileList) => void): UseFileDropTargetReturn {
  const [isDraggedOver, setIsDraggedOver] = useState(false)
  const dragCounterRef = useRef(0)

  const dropTargetProps: DropTargetProps = useMemo(
    () => ({
      onDragEnter: (event: ReactDragEvent) => {
        if (!event.dataTransfer.types.includes('Files')) return
        event.preventDefault()
        event.stopPropagation()
        dragCounterRef.current++
        if (dragCounterRef.current === 1) setIsDraggedOver(true)
      },
      onDragOver: (event: ReactDragEvent) => {
        if (!event.dataTransfer.types.includes('Files')) return
        event.preventDefault()
        event.stopPropagation()
        event.dataTransfer.dropEffect = 'copy'
      },
      onDragLeave: (event: ReactDragEvent) => {
        event.preventDefault()
        event.stopPropagation()
        dragCounterRef.current--
        if (dragCounterRef.current <= 0) {
          dragCounterRef.current = 0
          setIsDraggedOver(false)
        }
      },
      onDrop: (event: ReactDragEvent) => {
        event.preventDefault()
        event.stopPropagation()
        dragCounterRef.current = 0
        setIsDraggedOver(false)
        if (event.dataTransfer.files.length > 0) {
          onFilesDropped(event.dataTransfer.files)
        }
      },
    }),
    [onFilesDropped],
  )

  return {dropTargetProps, isDraggedOver}
}
